// ----------------------------------------------------------------------------
// module-cards.cjs — shared audio module card renderer
// Used by adapters that encounter tracker modules (MOD, XM, S3M, IT, DBM)
// to render a visual card with a mock tracker grid, save/download, and
// drag-drop to bucket.
// ----------------------------------------------------------------------------
// API:
//   createModuleCard(opts)
//     opts {
//       name         — display name for the card label
//       format       — module format string ('mod', 'xm', 's3m', 'it', 'dbm')
//       size         — file size in bytes
//       sampleCount  — number of samples extracted from the module
//       moduleData   — ArrayBuffer/Blob of the original module file (for save)
//       themeColors  — ctx.themeColors (value or function)
//       bucket       — ctx.bucket
//       source       — string id for bucket (e.g. 'raw', 'mod')
//       onAddAll     — optional function() called when "add all" is clicked
//     }
//
//   drawTrackerGrid(canvas, moduleData, format, size, sampleCount, themeColors)
//     Draws a mock tracker pattern grid on a canvas with module info overlay
// ----------------------------------------------------------------------------

const { getThemeDrawColors } = require('./draw-utils.cjs');

/**
 * Resolve theme colors from various forms (array, function, object).
 */
function _resolveColors(themeColors) {
   if (typeof themeColors === 'function') {
      const result = themeColors();
      if (Array.isArray(result)) {
         return { bg: result[0] || '#000000', fg: result[15] || result[result.length - 1] || '#e1e1e1', mid: result[8] || '#787878', dim: result[4] || '#383838', arr: result };
      }
      return _resolveColors(result);
   }
   if (Array.isArray(themeColors)) {
      return { bg: themeColors[0] || '#000000', fg: themeColors[15] || themeColors[themeColors.length - 1] || '#e1e1e1', mid: themeColors[8] || '#787878', dim: themeColors[4] || '#383838', arr: themeColors };
   }
   if (themeColors && themeColors.data) {
      const d = themeColors.data;
      const arr = Object.values(d);
      return { bg: d.c0 || '#000000', fg: d.cf || '#e1e1e1', mid: d.c8 || '#787878', dim: d.c4 || '#383838', arr };
   }
   if (themeColors && themeColors.c0) {
      return { bg: themeColors.c0, fg: themeColors.cf || '#e1e1e1', mid: themeColors.c8 || '#787878', dim: themeColors.c4 || '#383838', arr: Object.values(themeColors) };
   }
   // Fallback to global theme
   const { bg, fg, mid } = getThemeDrawColors();
   return { bg, fg, mid, dim: '#383838', arr: null };
}

/**
 * Detect module format from magic bytes.
 * Returns { format, numChannels, numRows } or null.
 */
function _detectModuleFormat(bytes) {
   if (!bytes || bytes.length < 4) return null;
   const id = String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);

   // Amiga MOD formats
   if (id === 'M.K.' || id === 'M!K!' || id === 'FLT4' || id === '8CHN' || id === '6CHN' || id === '2CHN') {
      let ch = 4;
      if (id === '8CHN') ch = 8;
      else if (id === '6CHN') ch = 6;
      else if (id === '2CHN') ch = 2;
      return { format: 'mod', numChannels: ch, numRows: 64 };
   }
   // MOD with 15-instrument header (no magic at offset 0, check offset 1080)
   if (bytes.length > 1084) {
      const id1080 = String.fromCharCode(bytes[1080], bytes[1081], bytes[1082], bytes[1083]);
      if (id1080 === 'M.K.' || id1080 === 'M!K!' || id1080 === 'FLT4' || id1080 === '8CHN') {
         let ch = 4;
         if (id1080 === '8CHN') ch = 8;
         return { format: 'mod', numChannels: ch, numRows: 64 };
      }
   }
   // S3M
   if (bytes[0] === 0x1A && bytes[1] === 0x10) {
      return { format: 's3m', numChannels: 8, numRows: 64 };
   }
   // IT
   if (id === 'IMPM') {
      return { format: 'it', numChannels: 8, numRows: 64 };
   }
   // XM
   if (id === 'Exte' || (bytes[0] === 0x45 && bytes[1] === 0x78 && bytes[2] === 0x74 && bytes[3] === 0x65)) {
      return { format: 'xm', numChannels: 8, numRows: 64 };
   }
   // DBM
   if (id === 'DBM0') {
      return { format: 'dbm', numChannels: 8, numRows: 64 };
   }
   return null;
}

/**
 * Draw a mock tracker pattern grid on a canvas.
 * Detects module format from the data and draws a realistic-looking
 * tracker grid with rows, columns, and "note" cells.
 * Overlays format label, file size, and sample count.
 */
function drawTrackerGrid(canvas, moduleData, format, size, sampleCount, themeColors) {
   if (!canvas) return;
   const c = canvas.getContext('2d');
   const W = canvas.width;
   const H = canvas.height;
   const { bg, fg, mid, dim, arr } = _resolveColors(themeColors);

   // Detect format from data
   let numRows = 64;
   let numChannels = 4;
   let detectedFormat = format || 'mod';

   if (moduleData) {
      try {
         const bytes = moduleData instanceof ArrayBuffer ? new Uint8Array(moduleData)
            : moduleData instanceof Uint8Array ? moduleData
            : (moduleData && moduleData.buffer) ? new Uint8Array(moduleData.buffer.slice(moduleData.byteOffset || 0, (moduleData.byteOffset || 0) + moduleData.byteLength))
            : null;
         if (bytes && bytes.length > 0) {
            const detected = _detectModuleFormat(bytes);
            if (detected) {
               numRows = detected.numRows;
               numChannels = detected.numChannels;
               detectedFormat = detected.format;
            }
         }
      } catch (e) { /* use defaults */ }
   }

   // Fill background
   c.fillStyle = bg;
   c.fillRect(0, 0, W, H);

   // Draw tracker grid
   const rowHeight = Math.max(1, Math.floor(H / numRows));
   const colWidth = Math.max(1, Math.floor(W / numChannels));
   const actualRows = Math.floor(H / rowHeight);
   const actualCols = Math.floor(W / colWidth);

   // Draw row backgrounds (every 4th row highlighted like a real tracker)
   for (let row = 0; row < actualRows; row++) {
      const y = row * rowHeight;
      if (row % 4 === 0) {
         c.fillStyle = dim;
         c.fillRect(0, y, W, rowHeight);
      }
   }

   // Draw row separator lines
   c.strokeStyle = mid;
   c.lineWidth = 0.5;
   c.globalAlpha = 0.3;
   for (let row = 0; row <= actualRows; row++) {
      const y = row * rowHeight;
      c.beginPath();
      c.moveTo(0, y);
      c.lineTo(W, y);
      c.stroke();
   }

   // Draw column separators
   for (let col = 0; col <= actualCols; col++) {
      const x = col * colWidth;
      c.beginPath();
      c.moveTo(x, 0);
      c.lineTo(x, H);
      c.stroke();
   }
   c.globalAlpha = 1;

   // Draw mock note data — deterministic pseudo-random pattern
   // that looks like a real tracker with notes, instruments, and effects
   const themeArr = arr || [bg, '#1e1e1e', '#252526', '#333334', dim, '#3f434e', '#444444', '#555555', mid, '#777777', '#888888', '#999999', '#aaaaaa', '#bbbbbb', '#cccccc', fg];

   for (let row = 0; row < actualRows; row++) {
      for (let col = 0; col < actualCols; col++) {
         // Deterministic "random" based on position
         const seed = (row * 31 + col * 17 + row * col * 7) % 100;
         if (seed < 30) {
            // Draw a "note" cell with varying color intensity
            const x = col * colWidth + 1;
            const y = row * rowHeight + 1;
            const cellW = colWidth - 2;
            const cellH = rowHeight - 1;

            // Use different theme colors for visual variety
            const colorIdx = (seed % 4 === 0) ? 15 : (seed % 4 === 1) ? 12 : (seed % 4 === 2) ? 9 : 6;
            c.fillStyle = themeArr[colorIdx] || fg;
            c.fillRect(x, y, cellW, cellH);
         } else if (seed < 40) {
            // Draw a lighter "effect" cell
            const x = col * colWidth + 1;
            const y = row * rowHeight + 1;
            c.fillStyle = mid;
            c.globalAlpha = 0.5;
            c.fillRect(x, y, colWidth - 2, Math.max(1, rowHeight - 2));
            c.globalAlpha = 1;
         }
      }
   }

   // Overlay format label in top-left with semi-transparent bg
   c.fillStyle = bg;
   c.globalAlpha = 0.8;
   c.fillRect(0, 0, W, 14);
   c.globalAlpha = 1;

   c.fillStyle = fg;
   c.font = 'bold 9px monospace';
   const fmtUpper = detectedFormat.toUpperCase();
   const sizeStr = size != null ? ` · ${(size / 1024).toFixed(1)}KB` : '';
   c.fillText(`${fmtUpper}${sizeStr}`, 3, 11);

   // Sample count at bottom-right
   if (sampleCount != null && sampleCount > 0) {
      c.fillStyle = bg;
      c.globalAlpha = 0.8;
      const txt = `${sampleCount} smp`;
      c.font = '8px monospace';
      const tw = c.measureText(txt).width;
      c.fillRect(W - tw - 4, H - 11, tw + 4, 11);
      c.globalAlpha = 1;
      c.fillStyle = mid;
      c.fillText(txt, W - tw - 2, H - 3);
   }
}

// Keep old name as alias for compatibility
const drawModuleInfo = drawTrackerGrid;

/**
 * Create a module card DOM element with canvas (tracker grid), save button,
 * add-to-folder, and drag-drop support.
 */
async function createModuleCard(opts) {
   const {
      name,
      format = 'mod',
      size,
      sampleCount,
      moduleData,
      themeColors,
      bucket,
      source = 'raw',
      onAddAll,
   } = opts;

   const sampleDiv = document.createElement('div');
   sampleDiv.classList.add('sample');
   sampleDiv.classList.add('module-card');
   sampleDiv.draggable = true;
   sampleDiv.dataset.sampleName = name;
   sampleDiv.innerHTML = `<label>${name}</label>`;

   // Canvas
   const canvas = document.createElement('canvas');
   canvas.classList.add('samplecanvas');
   canvas.width = 200;
   canvas.height = 75;
   sampleDiv.appendChild(canvas);

   // Draw the tracker grid with module info overlay
   drawTrackerGrid(canvas, moduleData, format, size, sampleCount, themeColors);

   // Create blob URL for the original module file (for save/drag)
   let modUrl = null;
   if (moduleData) {
      const blob = moduleData instanceof Blob ? moduleData : new Blob([moduleData], { type: 'application/octet-stream' });
      modUrl = URL.createObjectURL(blob);
      sampleDiv.__moduleUrl = modUrl;
   }
   sampleDiv.__moduleFormat = format;
   sampleDiv.__moduleName = name;

   // Save link — download the original module file
   if (modUrl) {
      const modExt = format || 'mod';
      const link = document.createElement('a');
      link.href = modUrl;
      link.download = `${name}.${modExt}`;
      link.textContent = 'save';
      sampleDiv.appendChild(link);
   }

   // add to bucket button — adds the module file itself to the bucket
   if (bucket && modUrl) {
      const addBtn = document.createElement('button');
      addBtn.textContent = 'add to bucket';
      addBtn.addEventListener('click', (e) => {
         e.stopPropagation();
         bucket.add({ name, wavLike: modUrl, source, fileType: 'module' });
      });
      sampleDiv.appendChild(addBtn);
   }

   // Add all samples button — if onAddAll callback provided
   if (onAddAll) {
      const addAllBtn = document.createElement('button');
      addAllBtn.textContent = `add ${sampleCount || 'all'} samples`;
      addAllBtn.addEventListener('click', (e) => {
         e.stopPropagation();
         onAddAll();
      });
      sampleDiv.appendChild(addAllBtn);
   }

   // Drag-drop support
   sampleDiv.addEventListener('dragstart', (event) => {
      event.dataTransfer.setData('text/plain', name);
      event.dataTransfer.setData('application/x-module-sample-name', name);
      if (modUrl) {
         event.dataTransfer.setData('application/x-module-url', modUrl);
      }
      event.dataTransfer.setData('application/x-module-format', format);
      event.dataTransfer.effectAllowed = 'copy';
   });

   // Click on canvas/label — if onAddAll provided, add all samples
   if (onAddAll) {
      sampleDiv.style.cursor = 'pointer';
      sampleDiv.title = `Click to add all ${sampleCount || ''} samples to bucket`;
      sampleDiv.addEventListener('click', (e) => {
         if (e.target === canvas || e.target.tagName === 'LABEL') {
            onAddAll();
         }
      });
   }

   return sampleDiv;
}

module.exports = {
   createModuleCard,
   drawTrackerGrid,
   drawModuleInfo,
   _detectModuleFormat,
};
